import { ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import type { TrashedEvent } from "@/services/api/events";
import { formatDateShort } from "@/utils/eventUtils";

interface TrashedEventStateProps {
  event: TrashedEvent;
  currentLocale: string;
  restoring: boolean;
  onRestore: () => void;
  onBack: () => void;
}

/**
 * JUB-157: opening the editor of an event that is in the Trash shows that
 * clearly, with Restore, instead of a generic "not found".
 */
export function TrashedEventState({ event, currentLocale, restoring, onRestore, onBack }: TrashedEventStateProps) {
  const { t } = useTranslation();

  return (
    <Card data-testid="editor-event-in-trash">
      <CardContent className="space-y-3 py-10 text-center">
        <p className="text-lg font-semibold">{t("events.trash.inTrashTitle", { name: event.name })}</p>
        <p className="text-sm text-muted-foreground">
          {event.restorable && event.retentionUntil
            ? t("events.trash.inTrashDescription", { date: formatDateShort(event.retentionUntil, currentLocale) })
            : t("events.trash.expired")}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={onRestore} disabled={!event.restorable || restoring} data-testid="editor-restore-event">
            <RotateCcw className="mr-2 h-4 w-4" />
            {t("events.trash.restore")}
          </Button>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
